import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Grid,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import * as XLSX from 'xlsx';
import { offices, subOffices, regions } from '../../data/formData';
import surveyApi from '../../services/surveyApi';

const YearlyReportTab = () => {
  // Filter states
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedOffice, setSelectedOffice] = useState('');
  const [selectedSubOffice, setSelectedSubOffice] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [InternalExternal, setInternalExternal] = useState('');

  // Fetched data state
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Years array (last 10 years)
  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  // Populate sub-offices based on selected office
  const currentSubOffices = selectedOffice ? 
    (subOffices[selectedOffice] || []) : 
    [];

  const handleFetchReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
        // Prepare filter parameters
        const params = {
          year: selectedYear,
          office: selectedOffice || undefined,
          subOffice: selectedSubOffice || undefined,
          region: selectedRegion || undefined,
          InternalExternal: InternalExternal || undefined
        };
       // Fetch report data
    const response = await surveyApi.getYearlyReport(params);
      
      if (response.data.success) {
        setReportData(response.data.reportData);
        setSnackbarOpen(true);
      } else {
        // Handle unsuccessful response
        setError(response.data.message || 'Failed to generate report');
      }
    } catch (error) {
      console.error('Error fetching yearly report:', error);
      setError(error.message || 'An error occurred while generating the report');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData) return;

    try {
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet([]);

      // Add headers to the worksheet
      XLSX.utils.sheet_add_aoa(ws, [['Yearly Report', `Year: ${selectedYear}`]], { origin: 'A1' });
      
      // Function to add section to worksheet
      const addSectionToWorksheet = (section, ws, startRow = 3) => {
        let currentRow = startRow;

        // Add section title
        XLSX.utils.sheet_add_aoa(ws, [[section.title]], { origin: `A${currentRow}` });
        currentRow += 2;

        if (section.data) {
          // Add data rows
          const dataRows = Object.entries(section.data).map(([key, value]) => [key, value]);
          XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: `A${currentRow}` });
          currentRow += dataRows.length + 2;
        }

        if (section.children) {
          section.children.forEach(child => {
            // Add subsection title
            XLSX.utils.sheet_add_aoa(ws, [[child.subTitle]], { origin: `A${currentRow}` });
            currentRow += 2;

            // Add data rows
            const dataRows = Object.entries(child.data).map(([key, value]) => [key, value]);
            XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: `A${currentRow}` });
            currentRow += dataRows.length + 2;
          });
        }

        return currentRow;
      };

      // Define sections from the report data
      const sections = [
        {
          title: 'I. Total Number of Clients',
          data: {
            'Total Clients who Completed Survey': reportData.totalClients || 0,
            'Total Transactions': reportData.totalTransactions || 0
          }
        },
        {
          title: 'II. Demographic Profile',
          children: [
            {
              subTitle: 'A. Age Distribution',
              data: reportData.ageDistribution || {}
            },
            {
              subTitle: 'B. Sex Distribution',
              data: reportData.sexDistribution || {}
            },
            {
              subTitle: 'C. Customer Type',
              data: reportData.customerType || {}
            }
          ]
        },
        {
          title: 'III. Citizen\'s Charter Responses',
          children: [
            {
              subTitle: 'A. Citizen\'s Charter Awareness',
              data: reportData.citizenCharterAwareness || {}
            },
            {
              subTitle: 'B. Citizen\'s Charter Visibility',
              data: reportData.citizenCharterVisibility || {}
            },
            {
              subTitle: 'C. Citizen\'s Charter Helpfulness',
              data: reportData.citizenCharterHelpfulness || {}
            }
          ]
        }
      ];

      // Add SQD responses as a section
      if (reportData.sqdResponses) {
        const sqdSection = {
          title: 'IV. Service Quality Dimensions (SQD)',
          children: Object.entries(reportData.sqdResponses).map(([key, value]) => ({
            subTitle: `${key}: ${value.question}`,
            data: value.responses || {}
          }))
        };
        sections.push(sqdSection);
      }

      // Add major concerns section
      if (reportData.majorConcerns) {
        sections.push({
          title: 'V. Major Concerns',
          data: reportData.majorConcerns || {}
        });
      }

      // Populate worksheet with sections
      let currentRow = 3;
      sections.forEach(section => {
        currentRow = addSectionToWorksheet(section, ws, currentRow);
      });

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Yearly Report');

      // Generate filename with filters
      let filename = `Yearly_Report_${selectedYear}`;
      if (selectedRegion) filename += `_${selectedRegion}`;
      if (selectedOffice) filename += `_${selectedOffice}`;
      if (selectedSubOffice) filename += `_${selectedSubOffice}`;
      if (InternalExternal) filename += `_${InternalExternal}`;
      filename += '.xlsx';

      // Export workbook
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('Failed to export report to Excel');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Yearly Report Generator
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Region</InputLabel>
            <Select
              value={selectedRegion}
              label="Region"
              onChange={(e) => {
                setSelectedRegion(e.target.value);
                setSelectedOffice('');
                setSelectedSubOffice('');
              }}
            >
              <MenuItem value="">All Regions</MenuItem>
              {regions.map(region => (
                <MenuItem key={region.value} value={region.value}>
                  {region.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Office</InputLabel>
            <Select
              value={selectedOffice}
              label="Office"
              onChange={(e) => {
                setSelectedOffice(e.target.value);
                setSelectedSubOffice('');
              }}
              disabled={!!selectedRegion}
            >
              <MenuItem value="">All Offices</MenuItem>
              {offices.map(office => (
                <MenuItem key={office.value} value={office.value}>
                  {office.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth disabled={!selectedOffice}>
            <InputLabel>Sub-Office</InputLabel>
            <Select
              value={selectedSubOffice}
              label="Sub-Office"
              onChange={(e) => setSelectedSubOffice(e.target.value)}
            >
              <MenuItem value="">All Sub-Offices</MenuItem>
              {currentSubOffices.map(subOffice => (
                <MenuItem key={subOffice.value} value={subOffice.value}>
                  {subOffice.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Client Type</InputLabel>
          <Select
            value={InternalExternal}
            label="Client Type"
            onChange={(e) => setInternalExternal(e.target.value)}
          >
            <MenuItem value="">All Client Types</MenuItem>
            <MenuItem value="Internal Client">Internal Client</MenuItem>
            <MenuItem value="External Client">External Client</MenuItem>
          </Select>
        </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button 
              variant="contained" 
              onClick={handleFetchReport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Generating Report...' : 'Generate Report'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={exportToExcel}
              disabled={!reportData || loading}
            >
              Export to Excel
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Report Summary Section */}
      {reportData && (
        <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>Report Summary</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle1">Client Statistics</Typography>
              <Typography>Total Clients: {reportData.totalClients || 0}</Typography>
              <Typography>Total Transactions: {reportData.totalTransactions || 0}</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle1">Demographic Highlights</Typography>
              <Typography>
                Gender: {reportData.sexDistribution?.Male || 0} Male, {reportData.sexDistribution?.Female || 0} Female
              </Typography>
              <Typography>
                Client Types: {Object.entries(reportData.customerType || {})
                  .map(([type, count]) => `${type}: ${count}`)
                  .join(', ')}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle1">Service Quality</Typography>
              <Typography>
                Top Reported Concerns: {Object.keys(reportData.majorConcerns || {}).slice(0, 2).join(', ')}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message="Report generated successfully"
      />
    </Box>
  );
};

export default YearlyReportTab;